import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './GroupsModelDemo.css';

/**
 * GroupsModelDemo — client-only sandbox mirroring the real Groups web map
 * (GroupsOrgWebView card/stem visuals) with two deliberate changes to test:
 *
 * 1. The org's root group-period and every period card are explicit nodes,
 *    and the branch [+] lives ON the period (gp) card — because nesting is
 *    gp → gp (`team_nested`), not group → group. "+ period" is a separate,
 *    smaller affordance on the group card.
 * 2. Groups carry membership_kind (anchor / extending) from
 *    docs/anchor-and-extending-memberships.md, chosen at creation
 *    (extending is the default).
 *
 * All data is mock and in-memory. No API calls.
 */

let seq = 1;
const nid = (p) => `${p}-${seq++}`;

const PERIOD_SUGGESTIONS = ['2026–2027', '2027–2028', 'Fall 2026', 'Spring 2027', 'Permanent'];

function buildExampleOrg() {
  const org = { id: 'org-1', name: 'School A' };
  const orgGp = { id: 'gp-org', groupId: 'org-1', period: 'Permanent', parentGpId: null };

  const groups = [
    { id: 'g-students', name: 'Students', kind: 'anchor' },
    { id: 'g-teachers', name: 'Teachers', kind: 'anchor' },
    { id: 'g-admin', name: 'Admin', kind: 'anchor' },
    { id: 'g-chess', name: 'Chess Club', kind: 'extending' },
    { id: 'g-sports', name: 'Sports', kind: 'extending' },
    { id: 'g-basketball', name: 'Basketball', kind: 'extending' },
    { id: 'g-roster', name: 'Team A Roster', kind: 'extending' },
    { id: 'g-coaches', name: 'Coaches', kind: 'anchor' }
  ];

  const gps = [
    orgGp,
    { id: 'gp-students-25', groupId: 'g-students', period: '2025–2026', parentGpId: 'gp-org' },
    { id: 'gp-students-26', groupId: 'g-students', period: '2026–2027', parentGpId: 'gp-org' },
    { id: 'gp-teachers-26', groupId: 'g-teachers', period: '2026–2027', parentGpId: 'gp-org' },
    { id: 'gp-admin-perm', groupId: 'g-admin', period: 'Permanent', parentGpId: 'gp-org' },
    { id: 'gp-chess-26', groupId: 'g-chess', period: '2026–2027', parentGpId: 'gp-students-26' },
    { id: 'gp-sports-perm', groupId: 'g-sports', period: 'Permanent', parentGpId: 'gp-org' },
    { id: 'gp-basketball-perm', groupId: 'g-basketball', period: 'Permanent', parentGpId: 'gp-sports-perm' },
    { id: 'gp-roster-f26', groupId: 'g-roster', period: 'Fall 2026', parentGpId: 'gp-basketball-perm' },
    { id: 'gp-coaches-perm', groupId: 'g-coaches', period: 'Permanent', parentGpId: 'gp-basketball-perm' }
  ];

  return { org, groups, gps };
}

/* ------------------------------------------------------------------ */

function KindPill({ kind }) {
  return (
    <span className={`gmd-pill gmd-pill--${kind}`}>{kind}</span>
  );
}

export default function GroupsModelDemo() {
  const [org, setOrg] = useState(null);
  const [groups, setGroups] = useState([]);
  const [gps, setGps] = useState([]);
  const [orgNameInput, setOrgNameInput] = useState('');
  const [selected, setSelected] = useState(null); // { type: 'gp'|'group', id }
  // createForm: { mode: 'branch', parentGpId } | { mode: 'period', groupId }
  const [createForm, setCreateForm] = useState(null);
  const [formName, setFormName] = useState('');
  const [formKind, setFormKind] = useState('extending');
  const [formPeriod, setFormPeriod] = useState('');

  const groupById = (id) => groups.find((g) => g.id === id) || null;
  const gpById = (id) => gps.find((x) => x.id === id) || null;

  const orgRootGp = useMemo(() => gps.find((x) => x.parentGpId === null) || null, [gps]);

  const gpPath = (gpId) => {
    const parts = [];
    let cur = gpById(gpId);
    while (cur) {
      const g = cur.groupId === org?.id ? org : groupById(cur.groupId);
      parts.unshift(`${g?.name || '?'} · ${cur.period}`);
      cur = cur.parentGpId ? gpById(cur.parentGpId) : null;
    }
    return parts;
  };

  /* ---------------- creation ---------------- */

  const createOrganization = (e) => {
    e.preventDefault();
    const name = orgNameInput.trim();
    if (!name) return;
    const newOrg = { id: nid('org'), name };
    setOrg(newOrg);
    setGps([{ id: nid('gp'), groupId: newOrg.id, period: 'Permanent', parentGpId: null }]);
    setOrgNameInput('');
  };

  const loadExample = () => {
    const ex = buildExampleOrg();
    setOrg(ex.org);
    setGroups(ex.groups);
    setGps(ex.gps);
    setSelected(null);
  };

  const openBranchForm = (parentGpId) => {
    setCreateForm({ mode: 'branch', parentGpId });
    setFormName('');
    setFormKind('extending');
    const parent = gpById(parentGpId);
    // Production default: nested team inherits the parent gp's period
    setFormPeriod(parent?.period || PERIOD_SUGGESTIONS[0]);
  };

  const openPeriodForm = (groupId) => {
    setCreateForm({ mode: 'period', groupId });
    setFormName('');
    setFormPeriod('');
  };

  const submitCreate = (e) => {
    e.preventDefault();
    if (!createForm) return;

    if (createForm.mode === 'branch') {
      const name = formName.trim();
      const period = formPeriod.trim();
      if (!name || !period) return;
      const group = { id: nid('g'), name, kind: formKind };
      const gp = { id: nid('gp'), groupId: group.id, period, parentGpId: createForm.parentGpId };
      setGroups((prev) => [...prev, group]);
      setGps((prev) => [...prev, gp]);
      setSelected({ type: 'gp', id: gp.id });
    } else {
      const period = formPeriod.trim();
      if (!period) return;
      const existing = gps.filter((x) => x.groupId === createForm.groupId);
      if (existing.some((x) => x.period === period)) {
        setCreateForm(null);
        return;
      }
      // New period on a group copies the parent link of its sibling gp —
      // this is the team_nested copy the rollover tool automates in production.
      const parentGpId = existing[0]?.parentGpId ?? orgRootGp?.id ?? null;
      const gp = { id: nid('gp'), groupId: createForm.groupId, period, parentGpId };
      setGps((prev) => [...prev, gp]);
      setSelected({ type: 'gp', id: gp.id });
    }
    setCreateForm(null);
  };

  /* ---------------- tree rendering ---------------- */

  const childGroupsOfGp = (gpId) => {
    const childGps = gps.filter((x) => x.parentGpId === gpId);
    const byGroup = new Map();
    childGps.forEach((x) => {
      if (!byGroup.has(x.groupId)) byGroup.set(x.groupId, []);
      byGroup.get(x.groupId).push(x);
    });
    return Array.from(byGroup.entries()).map(([groupId, groupGps]) => ({
      group: groupById(groupId),
      gps: groupGps
    }));
  };

  const renderGpCard = (gp, { isOrgRoot = false } = {}) => {
    const isSelected = selected?.type === 'gp' && selected.id === gp.id;
    return (
      <div className="gmd-card-slot">
        <button
          type="button"
          className={`gmd-card gmd-card--period${isSelected ? ' is-selected' : ''}${isOrgRoot ? ' gmd-card--org-gp' : ''}`}
          onClick={() => setSelected({ type: 'gp', id: gp.id })}
          title="Group-period (gp) — members enroll here; nesting branches from here"
        >
          <span className="gmd-card-name">{gp.period}</span>
          <span className="gmd-card-meta">
            <span className="gmd-badge gmd-badge--period">period</span>
            {isOrgRoot && <span className="gmd-meta-text">org shell</span>}
          </span>
        </button>
        <button
          type="button"
          className="gmd-add gmd-add--branch"
          onClick={(e) => {
            e.stopPropagation();
            openBranchForm(gp.id);
          }}
          title={`Branch a nested team off this group-period (${gp.period})`}
          aria-label="Branch nested team from this group-period"
        >
          +
        </button>
      </div>
    );
  };

  const renderGroupBranch = (group, groupGps) => (
    <div className="gmd-branch" key={group.id}>
      <div className="gmd-card-slot">
        <button
          type="button"
          className={`gmd-card gmd-card--group${
            selected?.type === 'group' && selected.id === group.id ? ' is-selected' : ''
          }`}
          onClick={() => setSelected({ type: 'group', id: group.id })}
          title="Group — durable identity across periods"
        >
          <span className="gmd-card-name">{group.name}</span>
          <span className="gmd-card-meta">
            <KindPill kind={group.kind} />
          </span>
        </button>
        <button
          type="button"
          className="gmd-add gmd-add--period"
          onClick={(e) => {
            e.stopPropagation();
            openPeriodForm(group.id);
          }}
          title={`Add another period on ${group.name} (copies the parent link)`}
          aria-label={`Add another period on ${group.name}`}
        >
          +⏱
        </button>
      </div>
      <div className="gmd-stem" aria-hidden />
      <div className="gmd-child-row">
        {groupGps.map((gp) => (
          <div className="gmd-child-cell" key={gp.id}>
            <div className="gmd-stem gmd-stem--to-card" aria-hidden />
            {renderGpCard(gp)}
            {childGroupsOfGp(gp.id).length > 0 && (
              <>
                <div className="gmd-stem" aria-hidden />
                <div className="gmd-child-row">
                  {childGroupsOfGp(gp.id).map(({ group: cg, gps: cgps }) => (
                    <div className="gmd-child-cell" key={cg.id}>
                      <div className="gmd-stem gmd-stem--to-card" aria-hidden />
                      {renderGroupBranch(cg, cgps)}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  /* ---------------- inspector ---------------- */

  const renderInspector = () => {
    if (!selected) {
      return (
        <p className="gmd-muted">
          Click any card to inspect it. Green <strong>+</strong> on a <em>period card</em> branches a
          nested team off that group-period. <strong>+⏱</strong> on a <em>group card</em> adds another
          period to the same group.
        </p>
      );
    }
    if (selected.type === 'gp') {
      const gp = gpById(selected.id);
      if (!gp) return null;
      const g = gp.groupId === org?.id ? { name: org.name, kind: 'anchor' } : groupById(gp.groupId);
      return (
        <>
          <h4>{g?.name} · {gp.period}</h4>
          <p className="gmd-inspector-path">{gpPath(gp.id).join('  ›  ')}</p>
          <p className="gmd-muted">
            This is a <strong>group-period (gp)</strong> — the context row. Members enroll here
            (UGP), apps and permissions attach here, and nesting is gp → gp (<code>team_nested</code>).
            That's why the branch <strong>+</strong> lives on this card: a nested team must know
            <em> which period's context</em> it hangs under, not just which group.
          </p>
        </>
      );
    }
    const g = groupById(selected.id);
    if (!g) return null;
    const gGps = gps.filter((x) => x.groupId === g.id);
    return (
      <>
        <h4>{g.name} <KindPill kind={g.kind} /></h4>
        <p className="gmd-muted">
          This is the <strong>group</strong> — the durable identity that threads{' '}
          {gGps.length} period{gGps.length === 1 ? '' : 's'} ({gGps.map((x) => x.period).join(', ') || 'none'})
          together. Name and membership kind live here once, so they can't drift between years.
          {g.kind === 'anchor'
            ? ' Anchor: members act as this role — sign-in identity, switcher, base permissions.'
            : ' Extending: members just belong — never a sign-in context; surfaces on the anchor profile.'}
        </p>
      </>
    );
  };

  /* ---------------- render ---------------- */

  return (
    <div className="gmd-page">
      <header className="gmd-header">
        <h1>Groups web map — gp-first branching demo</h1>
        <p>
          Mirrors the real Groups web view, with the <strong>+</strong> moved onto the
          period (gp) cards — branching happens where <code>team_nested</code> actually attaches.
          Mock data only.{' '}
          <Link to="/membership-model-guide">Membership model guide</Link>.
        </p>
      </header>

      {!org ? (
        <div className="gmd-empty">
          <form onSubmit={createOrganization} className="gmd-org-form">
            <h3>Create an organization</h3>
            <p className="gmd-muted">
              Organizations are created on the <strong>Permanent</strong> period — the long-lived
              shell everything else branches from.
            </p>
            <input
              value={orgNameInput}
              onChange={(e) => setOrgNameInput(e.target.value)}
              placeholder="e.g. School A"
              autoFocus
            />
            <div className="gmd-org-form-actions">
              <button type="submit" className="gmd-btn">Create organization</button>
              <button type="button" className="gmd-btn gmd-btn--ghost" onClick={loadExample}>
                Or load a full example
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="gmd-layout">
          <div className="gmd-map">
            <div className="gmd-tree">
              <div className="gmd-card-slot">
                <div className="gmd-card gmd-card--org">
                  <span className="gmd-card-name">{org.name}</span>
                  <span className="gmd-card-meta">
                    <span className="gmd-badge gmd-badge--org">organization</span>
                  </span>
                </div>
              </div>
              {orgRootGp && (
                <>
                  <div className="gmd-stem" aria-hidden />
                  {renderGpCard(orgRootGp, { isOrgRoot: true })}
                  {childGroupsOfGp(orgRootGp.id).length > 0 && (
                    <>
                      <div className="gmd-stem" aria-hidden />
                      <div className="gmd-child-row">
                        {childGroupsOfGp(orgRootGp.id).map(({ group: g, gps: ggps }) => (
                          <div className="gmd-child-cell" key={g.id}>
                            <div className="gmd-stem gmd-stem--to-card" aria-hidden />
                            {renderGroupBranch(g, ggps)}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <p className="gmd-map-hint">
              Every branch hangs off a <em>period card</em> — the org's Permanent shell included.
              Group cards never take branches directly; they only take more periods (+⏱).
            </p>
          </div>

          <aside className="gmd-side">
            <div className="gmd-panel">
              <h3>Inspector</h3>
              {renderInspector()}
            </div>

            {createForm && (
              <div className="gmd-panel gmd-panel--form">
                {createForm.mode === 'branch' ? (
                  <>
                    <h3>Branch nested team</h3>
                    <p className="gmd-inspector-path">
                      under {gpPath(createForm.parentGpId).join(' › ')}
                    </p>
                    <form onSubmit={submitCreate} className="gmd-form">
                      <label>Team name
                        <input value={formName} onChange={(e) => setFormName(e.target.value)} autoFocus placeholder="e.g. Choir" />
                      </label>
                      <label>Period for the new team's gp
                        <input
                          value={formPeriod}
                          onChange={(e) => setFormPeriod(e.target.value)}
                          list="gmd-periods"
                        />
                      </label>
                      <p className="gmd-form-note">
                        Defaults to the parent gp's period ({gpById(createForm.parentGpId)?.period}) —
                        same as production's nested-team default.
                      </p>
                      <div className="gmd-kind-toggle">
                        <label className={formKind === 'extending' ? 'gmd-kind is-active' : 'gmd-kind'}>
                          <input type="radio" checked={formKind === 'extending'} onChange={() => setFormKind('extending')} />
                          <span><strong>Extending</strong> — members just belong. Default.</span>
                        </label>
                        <label className={formKind === 'anchor' ? 'gmd-kind is-active' : 'gmd-kind'}>
                          <input type="radio" checked={formKind === 'anchor'} onChange={() => setFormKind('anchor')} />
                          <span><strong>Anchor</strong> — sign-in identity. Rare; provisioning-time.</span>
                        </label>
                      </div>
                      <div className="gmd-form-actions">
                        <button type="submit" className="gmd-btn">Create</button>
                        <button type="button" className="gmd-btn gmd-btn--ghost" onClick={() => setCreateForm(null)}>Cancel</button>
                      </div>
                    </form>
                  </>
                ) : (
                  <>
                    <h3>Add period on {groupById(createForm.groupId)?.name}</h3>
                    <form onSubmit={submitCreate} className="gmd-form">
                      <label>Period
                        <input
                          value={formPeriod}
                          onChange={(e) => setFormPeriod(e.target.value)}
                          list="gmd-periods"
                          autoFocus
                          placeholder="e.g. 2027–2028"
                        />
                      </label>
                      <p className="gmd-form-note">
                        The new gp copies the sibling's parent link (<code>team_nested</code>) — the
                        step the rollover tool automates. Same group identity, new term context.
                      </p>
                      <div className="gmd-form-actions">
                        <button type="submit" className="gmd-btn">Add period</button>
                        <button type="button" className="gmd-btn gmd-btn--ghost" onClick={() => setCreateForm(null)}>Cancel</button>
                      </div>
                    </form>
                  </>
                )}
                <datalist id="gmd-periods">
                  {PERIOD_SUGGESTIONS.map((p) => <option key={p} value={p} />)}
                </datalist>
              </div>
            )}

            <div className="gmd-panel gmd-panel--legend">
              <h3>Legend</h3>
              <ul className="gmd-legend">
                <li><span className="gmd-legend-swatch gmd-legend-swatch--group" /> Group — durable identity (name + kind live here)</li>
                <li><span className="gmd-legend-swatch gmd-legend-swatch--period" /> Group-period (gp) — dashed; the context members enroll in</li>
                <li><span className="gmd-legend-add">+</span> on a gp — branch a nested team off <em>this</em> context</li>
                <li><span className="gmd-legend-add gmd-legend-add--period">+⏱</span> on a group — another period, same identity</li>
              </ul>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
